/** Color-space conversions + small numeric helpers. Pure, dependency-free. */

import type { HSL, RGB } from './types.js';

export function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

/** Round + clamp to a valid 0–255 channel. */
export function toChannel(n: number): number {
  return clamp(Math.round(n), 0, 255);
}

/** Parse #rgb / #rrggbb (with or without leading #). Throws on malformed input. */
export function hexToRgb(hex: string): RGB {
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (!/^[0-9a-fA-F]{6}$/.test(h)) {
    throw new Error(`invalid hex color: "${hex}"`);
  }
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

export function rgbToHex({ r, g, b }: RGB): string {
  const to = (n: number): string => toChannel(n).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

export function rgbToHsl({ r, g, b }: RGB): HSL {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const d = max - min;

  if (d === 0) {
    return { h: 0, s: 0, l };
  }

  const s = d / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === rn) {
    h = ((gn - bn) / d) % 6;
  } else if (max === gn) {
    h = (bn - rn) / d + 2;
  } else {
    h = (rn - gn) / d + 4;
  }
  h *= 60;
  if (h < 0) h += 360;
  return { h, s, l };
}

export function hslToRgb({ h, s, l }: HSL): RGB {
  const hn = ((h % 360) + 360) % 360;
  const sn = clamp(s, 0, 1);
  const ln = clamp(l, 0, 1);
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const x = c * (1 - Math.abs(((hn / 60) % 2) - 1));
  const m = ln - c / 2;

  let rp = 0;
  let gp = 0;
  let bp = 0;
  if (hn < 60) {
    rp = c;
    gp = x;
  } else if (hn < 120) {
    rp = x;
    gp = c;
  } else if (hn < 180) {
    gp = c;
    bp = x;
  } else if (hn < 240) {
    gp = x;
    bp = c;
  } else if (hn < 300) {
    rp = x;
    bp = c;
  } else {
    rp = c;
    bp = x;
  }
  return {
    r: toChannel((rp + m) * 255),
    g: toChannel((gp + m) * 255),
    b: toChannel((bp + m) * 255),
  };
}

/** Rotate hue by `deg` degrees, preserving s/l. */
export function rotateHue(rgb: RGB, deg: number): RGB {
  const hsl = rgbToHsl(rgb);
  return hslToRgb({ ...hsl, h: hsl.h + deg });
}

/** Set saturation, preserving h/l. */
export function withSaturation(rgb: RGB, s: number): RGB {
  const hsl = rgbToHsl(rgb);
  return hslToRgb({ ...hsl, s: clamp(s, 0, 1) });
}

/** Set lightness, preserving h/s. */
export function withLightness(rgb: RGB, l: number): RGB {
  const hsl = rgbToHsl(rgb);
  return hslToRgb({ ...hsl, l: clamp(l, 0, 1) });
}
